import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Empty } from "antd";

// Coarse row-level diff between two rows arrays. For each row that differs we
// emit `{col, before, after}` cells. If rows are added / removed we surface
// a marker so the audit view still tells the reviewer *something* useful.
function diffContents(before, after) {
    const beforeRows = (before && before.rows) || [];
    const afterRows = (after && after.rows) || [];
    const changes = [];
    const max = Math.max(beforeRows.length, afterRows.length);
    for (let i = 0; i < max; i++) {
        const b = beforeRows[i] || null;
        const a = afterRows[i] || null;
        if (!b && a) {
            changes.push({ label: `Row ${i + 1} added`, before: "—", after: JSON.stringify(a) });
            continue;
        }
        if (b && !a) {
            changes.push({ label: `Row ${i + 1} removed`, before: JSON.stringify(b), after: "—" });
            continue;
        }
        if (b && a) {
            const keys = new Set([...Object.keys(b || {}), ...Object.keys(a || {})]);
            keys.forEach(key => {
                if ((b[key] || "") !== (a[key] || "")) {
                    changes.push({
                        label: `Row ${i + 1} · ${key}`,
                        before: b[key] || "—",
                        after: a[key] || "—",
                    });
                }
            });
        }
    }
    return changes;
}

/**
 * History pane. Renders each PolicyChangeLog entry as an inline diff so the
 * credit team can quickly review who changed what.
 */
const PolicyHistory = ({ entries }) => {
    if (!entries || entries.length === 0) {
        return (
            <div className={"policy-hub-history__empty"}>
                <Empty description={"No changes yet"} />
            </div>
        );
    }
    return (
        <div className={"policy-hub-history"}>
            {entries.map(entry => {
                const changes = diffContents(entry.before, entry.after);
                return (
                    <div key={entry.id} className={"policy-hub-history__entry"}>
                        <div className={"policy-hub-history__row"}>
                            <div>
                                <span className={"policy-hub-history__actor"}>
                                    {entry.changed_by_label || "system"}
                                </span>
                                <span
                                    className={"policy-hub-history__ver"}
                                    style={{ marginLeft: 8 }}
                                >
                                    v{entry.version_from} → v{entry.version_to}
                                </span>
                            </div>
                            <span className={"policy-hub-history__time"}>
                                {moment(entry.changed_at).format(
                                    "DD MMM YYYY, HH:mm"
                                )}
                            </span>
                        </div>
                        {entry.note ? (
                            <div className={"policy-hub-history__note"}>
                                “{entry.note}”
                            </div>
                        ) : null}
                        {changes.length === 0 ? (
                            <div style={{ fontSize: 12, color: "#8a93a3" }}>
                                No content changes recorded.
                            </div>
                        ) : (
                            <div className={"policy-hub-history__diff"}>
                                <div className={"policy-hub-history__diff-head"}>
                                    Cell
                                </div>
                                <div className={"policy-hub-history__diff-head"}>
                                    Before
                                </div>
                                <div className={"policy-hub-history__diff-head"}>
                                    After
                                </div>
                                {changes.map((c, idx) => (
                                    <React.Fragment key={idx}>
                                        <div style={{ fontWeight: 600, fontSize: 11.5 }}>
                                            {c.label}
                                        </div>
                                        <div
                                            className={
                                                "policy-hub-history__diff-before"
                                            }
                                        >
                                            {c.before}
                                        </div>
                                        <div
                                            className={
                                                "policy-hub-history__diff-after"
                                            }
                                        >
                                            {c.after}
                                        </div>
                                    </React.Fragment>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

PolicyHistory.propTypes = {
    entries: PropTypes.array,
};

export default PolicyHistory;
